'use client';
import clsx from 'clsx';
import React, { useRef, useState, useEffect, useMemo } from 'react';
import classes from './RevealOnView.module.css.mjs';
import { usePrefersReducedMotion } from './RevealOnView.utils.mjs';

const RevealOnView = ({
  component: Component = "div",
  delay = 0,
  enabled = false,
  duration = 800,
  ease = "ease-out",
  opacityFrom = 0,
  transformFrom = "translateY(20px)",
  once = true,
  threshold = 0.15,
  rootMargin = "0px 0px -10% 0px",
  className,
  children,
  style: userStyle
}) => {
  if (!enabled) {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, children);
  }
  const prefersReduce = usePrefersReducedMotion();
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (once) {
            if (entry.isIntersecting) {
              setVisible(true);
              io.disconnect();
              break;
            }
          } else {
            setVisible(entry.isIntersecting);
          }
        }
      },
      { threshold, rootMargin }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [once, threshold, rootMargin]);
  if (!enabled || prefersReduce) return /* @__PURE__ */ React.createElement(React.Fragment, null, children);
  const Comp = Component;
  const style = useMemo(() => {
    const s = userStyle || {};
    if (delay) s.transitionDelay = `${delay}ms`;
    if (duration != null) s["--rov-duration"] = `${duration}ms`;
    if (ease) s["--rov-ease"] = ease;
    if (opacityFrom != null) s["--rov-opacity-from"] = String(opacityFrom);
    if (transformFrom) s["--rov-transform-from"] = transformFrom;
    return s;
  }, [delay, duration, ease, opacityFrom, transformFrom]);
  return React.createElement(
    Comp,
    {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref,
      className: clsx(classes.kohlerReveal, visible ? classes.kohlerRevealVisible : classes.kohlerRevealHidden, className),
      style
    },
    children
  );
};

export { RevealOnView };
